import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { withOptimizely } from '@optimizely/react-sdk';

import Icon from '@klv/atoms/icon';
import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';
import { UniversalMenuItemWithOptimizely } from '@klv/molecules/universal-menu-item/withOptimizely';
import { convertNamedColorToColorVariant, generateClassName } from '@klv/shared/utils/functions';
import { UniversalNavigationDrawer } from './children/drawer';
import { UniversalNavigationMobile } from './children/mobile';
import { gtmClick, loginActions } from './utils';
import { DropdownButton } from '@klv/molecules/dropdown-button';
import * as gridStyles from '@klv/utils/grid/styles/grid.module.css';
import * as styles from './styles/universalNavigation.module.css';

import type { IUniversalNavigation } from './types';
import type { WithOptimizelyProps } from '@optimizely/react-sdk';

const handleResize = (mobileMenuRef: React.RefObject<HTMLDialogElement | null>) => {
  if (
    window.innerWidth >= 1024
    && mobileMenuRef?.current?.open
  ) {
    mobileMenuRef.current.close();
  }
};

export const UniversalNavigation = withOptimizely(({
  optimizely,
  optimizelyReadyTimeout,
  isServerSide,
  academyLoggedIn,
  children,
  className = '',
  color = 'lightSand',
  ctas = [],
  desktopLogoIconName = 'Logo',
  homeLink = '/',
  invertOnHover = true,
  menuItems = [],
  menuStyle = 'default',
  navigationStyle = 'default',
  platformActions = [],
  scrollDirection,
  siteActions = [],
  utilityItems = [],
  path = '',
  showSubnav = false,
  ...props
}: IUniversalNavigation & WithOptimizelyProps) => {
  const { t } = useTranslation();

  const mobileMenuRef = useRef<HTMLDialogElement>(null);

  const onResize = useCallback(() => handleResize(mobileMenuRef), [mobileMenuRef]);
  
  useEffect(() => {
    window.addEventListener('resize', onResize);

    return window.removeEventListener('resize', onResize);
  }, [onResize]);
  
  const isDefaultNav = navigationStyle !== 'minimal';
  
  /**** 
   * Begin Optimizely test "2024q3_navigation_hiw_demo_cta" 
   *****/
  // optimizely test for How it works in the top navigation
  const decision = optimizely?.decide('2024q3_navigation_hiw_demo_cta');
  const hasHowItWorksTest = (decision?.enabled || false) && desktopLogoIconName === 'Logo';

  // Add How it works to the top navigation if the test is enabled and the menu item doesn't already exist
  if (hasHowItWorksTest && !menuItems.find((e) => e.label === 'How it works')) {
    menuItems.splice(-1, 0, {
      'id': 'how-it-works',
      'label': 'How it works',
      'locale': 'en-US',
      'url': 'https://www.klaviyo.com/product/platform-tour',
      'childItems': [],
    });
    // Remove the last item in the menu
    menuItems[0].childItems?.pop();
  }
  useEffect(() => {
    const howItWorksLinks = document.querySelectorAll('a[href="https://www.klaviyo.com/product/platform-tour"]');
    const optimizelyTrack = () => {
      optimizely?.track('global_nav_link_click_demo');
    };
    howItWorksLinks.forEach((link) => {
      link.addEventListener('click', optimizelyTrack);
    });

    return () => {
      howItWorksLinks.forEach((link) => {
        link.removeEventListener('click', optimizelyTrack);
      });
    };
  }, [optimizely]);
  /**** 
   * End Optimizely test "2024q3_navigation_hiw_demo_cta" 
   *****/
  
  return (
    <div
      className={generateClassName([
        className,
        styles.universalNavigation,
        invertOnHover && styles.invertOnHover,
        styles[convertNamedColorToColorVariant(color)],
        scrollDirection === 'down' && styles.scrolled,
        showSubnav && styles.showSubnav,
      ])}
      {...props}
    >
      <div className={generateClassName([gridStyles.grid, styles.container])}>
        <a 
          title={desktopLogoIconName !== 'Logo' ? t('organisms_navigation_powerUpLogo_label') : t('organisms_navigation_logo_label')}
          href={ desktopLogoIconName !== 'Logo' ? t('organisms_navigation_powerUpLogo_href') : homeLink}
          className={generateClassName([styles.logoContainer, desktopLogoIconName !== 'Logo' ? styles.helpCenterLogo : ''])}
        >
          <Icon
            className={generateClassName([styles.logo])}
            color='currentColor'
            iconName={desktopLogoIconName}
            size={3.6}
          />
          <Icon
            className={styles.flag}
            color='currentColor'
            iconName='BugMarkFilled'
            size={3}
          />
        </a>
        {isDefaultNav && (
          <>
            <nav className={generateClassName([styles.nav])}>
              <ul className={styles.list}>
                {menuItems.map(({ childItems, id, label, url: href }) => (
                  <li
                    className={generateClassName([
                      styles.listItem,
                      menuStyle === 'default' && childItems?.length && styles.hasChildren,
                    ])}
                    key={id}
                  >
                    <UniversalMenuItemWithOptimizely
                      aria-current={
                        (childItems?.some(({ url }) => url === path) && 'true')
                        || href === path
                        || undefined
                      }
                      as={(href === '#' || href === '') ? 'div' : undefined}
                      className={generateClassName([
                        styles.link,
                        childItems?.length && styles.hasChildren,
                      ])}
                      href={(href === '#' || href === '') ? undefined : href}
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        gtmClick({ childItem: label });
                        if ((href === '#' || href === '')) {
                          (e?.target as HTMLElement)?.blur();
                        }
                      }}
                      tabIndex={(href === '#' || href === '') ? 0 : undefined}
                      optimizelyLabel={label}
                    >
                      <span>{label}</span>
                    </UniversalMenuItemWithOptimizely>
                    {menuStyle === 'default' && (
                      <UniversalNavigationDrawer
                        className={styles.drawer}
                        menuItems={childItems}
                        parentLabel={label}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </nav>            
            <ul className={generateClassName([styles.list, styles.ctaList])}>
            {academyLoggedIn ? (
              <li className={styles.ctaListItem}>
                <DropdownButton 
                  children={<a href={t('navigation_ecosystem_ctas_academy_profile_href')}>{t('navigation_ecosystem_ctas_academy_profile_label')}</a>}
                  dropdownItems={[
                    <a href='https://v2.accounts.accredible.com/retrieve-credentials' target='_blank'>
                      {t('navigation_ecosystem_ctas_academy_myCredentials_label')}
                    </a>,
                    <button
                      className={styles.academyLogoutButton} 
                      onClick={()=> {
                        localStorage.removeItem('skilljarUser');
                        localStorage.removeItem('viewedResources');
                        sessionStorage.removeItem('lastViewedAcademyContent');
                        window.location.replace(`${process.env.GATSBY_IFRAME_SOURCE}${t('navigation_ecosystem_ctas_academy_logOut_href')}`);
                      }}
                    >
                      {t('navigation_ecosystem_ctas_academy_logOut_label')}
                    </button>,
                  ]}
                  dropdownName='profile-actions'
                />
              </li>
            ) : (
              ctas.map(({ id, label, variant, url, ...rest }) =>  (
                <li className={styles.ctaListItem} key={id}>
                  <UniversalMenuItem
                    className={generateClassName([
                      styles.cta,
                      variant && styles[variant],
                    ])}
                    href={url}
                    label={label}
                    onClick={() => loginActions({ childItem: label })}
                    {...rest}
                  />
                </li>
              )))}
            </ul>
            <div className={styles.mobileMenuToggle}>
              <button aria-label="Toggle mobile menu" className={styles.toggleButton} onClick={() => mobileMenuRef?.current?.showModal()}>
                <svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="2" rx="1" fill="currentColor"/>
                  <rect y="8" width="24" height="2" rx="1" fill="currentColor"/>
                  <rect y="16" width="24" height="2" rx="1" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
      {isDefaultNav && (
        <>
          <UniversalNavigationMobile
            ctas={ctas}
            menuItems={menuItems}
            menuStyle={menuStyle}
            path={path}
            platformActions={platformActions}
            ref={mobileMenuRef}
            scrollDirection={scrollDirection}
            siteActions={siteActions}
            utilityItems={utilityItems}
          />
          {children}
        </>
      )}
    </div>
  );
});
